import { Checkbox, Select, Spin } from 'antd'
import { debounce } from 'lodash'
import { ReactElement, useMemo, useRef, useState } from 'react'
import { DefaultOptionType } from 'antd/lib/select'

import { useAppState } from '../../state'
import { removeIconSVG } from '../../assets/svg/removeIcon'
import { MultiTenantSelectorContainer } from '../../atom/select'
import { magnifySVG } from '../../assets/svg/magnify'
import { GetUsers } from '../../services/data-provider/users'
import styled from 'styled-components'
import { theme } from 'theme'

export const RfidSelectorContainer = styled.div`
  .ant-select-selector {
    height: 39px !important;
    border-right: 1px solid ${theme.colors.fadedGrayBlue} !important;
    border-left-color: transparent !important;
    display: flex;
    align-items: center;
    padding-left: 14px !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .ant-select-selection-overflow {
    margin-left: -7px;
  }

  /* Show the selected value / summary, but make it look like plain text */
  .ant-select-selection-item {
    background: transparent;
    border: 0;
    padding: 0;
    margin-inline-end: 0;
  }

  .ant-select-selection-item-content {
    color: ${theme.colors.darkGray};
    font-size: 14px;
  }

  .ant-select-selection-placeholder {
    color: ${theme.colors.darkGray};
    font-size: 14px;
  }

  .ant-select-arrow {
    display: none !important; /* no default arrow */
  }

  .ant-select-item {
    padding: 6px 10px !important;
  }

  .ant-checkbox-wrapper {
    display: flex;
    align-items: center;
  }
`

interface RfidRef {
  id: number
  rfidNumber: string
}

interface Props {
  onOptionsChange: (options: RfidRef[]) => void
  onClear: () => void
  loading?: boolean
  longerWidth: boolean
  dropdownClassName?: string
}

export const MultiRfidSelector: React.FC<Props> = ({
  onOptionsChange,
  onClear,
  loading,
  longerWidth,
  dropdownClassName,
}) => {
  const { IsDesktop, IsLaptop, IsMobile, IsTablet, isMockUpEnabled } = useAppState()

  const [rfids, setRfids] = useState<RfidRef[]>([])
  const [selectedOptions, setSelectedOptions] = useState<RfidRef[]>([])
  const [dirty, setDirty] = useState(false)
  const [rfidOptions, setRfidOptions] = useState<RfidRef[]>([])
  const fetchRef = useRef(0)
  const [fetching, setFetching] = useState(false)

  const isDesktop = IsDesktop()
  const isLaptop = IsLaptop()
  const isMobile = IsMobile()
  const isTablet = IsTablet()

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {

      if (value.length >= 1) {
        fetchRef.current += 1
        const fetchId = fetchRef.current
        setFetching(true)

        GetUsers(1, isMockUpEnabled, undefined, undefined, undefined, undefined, undefined, value)
          .then((usersResp: any) => {

            if (fetchId !== fetchRef.current) {
              return
            }

            const allRfids: RfidRef[] = (usersResp.data || [])
              .flatMap((u: any) => u.rfids || [])
              .filter((r: any) => !!r && !!r.rfid_number)
              .map((r: any) => ({
                id: r.id,
                rfidNumber: String(r.rfid_number).trim(),
              }))


            const uniqueById = Array.from(new Map(allRfids.map((r) => [r.id, r])).values())

            const filtered = uniqueById.filter((r) =>
              r.rfidNumber.toLowerCase().includes(value.toLowerCase()),
            )


            setRfids(filtered)
            setRfidOptions((prev) => [
              ...prev,
              ...filtered.filter((r) => !prev.some((p) => p.id === r.id)),
            ])

            setFetching(false)
          })
          .catch((err) => {
            setFetching(false)
          })
      }
    }

    return debounce(loadOptions, 800)
  }, [isMockUpEnabled])


  const handleCheckBoxClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (dirty) {
      e.stopPropagation()
    }
    setDirty(false)
  }

  const renderOption = (rfid: RfidRef): ReactElement => (
    <Select.Option key={rfid.id} value={rfid.rfidNumber} label={rfid.rfidNumber}>
      <Checkbox
        onClick={handleCheckBoxClick}
        checked={selectedOptions.findIndex((selected) => selected.id === rfid.id) > -1}
      >
        {rfid.rfidNumber}
      </Checkbox>
    </Select.Option>
  )

  const handleChange = (value: string[]) => {
    setDirty(true)
    const normalized = value.map((v) => v.trim())
    const selected = rfidOptions.filter((r) => normalized.includes(r.rfidNumber))
    const deduped = selected.filter((obj, index, self) => index === self.findIndex((t) => t.id === obj.id))
    setSelectedOptions(deduped)
    onOptionsChange(deduped)
  }


  const handleOnClear = () => {
    setSelectedOptions([])
    onClear()
  }

  const canCombineBorderRight = true
  const canCombineBorderLeft = false
  const canCombineBorderRightAndLeft = false

  const handlePlaceholder = () => {
    const selected = selectedOptions.length
    return selected === 1 ? selectedOptions[0].rfidNumber : `${selected} RFIDs Selected`
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setDirty(false)
    e.stopPropagation()
  }

  const filterOption = (input: string, option: DefaultOptionType | undefined) => {
    if (selectedOptions.some((selectedOption) => `${selectedOption.id}` === option?.key)) {
      return true
    }
    return (option?.label as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  const renderRfidList = (list: RfidRef[]) => {
    const optionsWithSelected = [
      ...selectedOptions,
      ...list.filter((rfid) => !selectedOptions.some((option) => option.id === rfid.id)),
    ]
    return optionsWithSelected.map((v) => renderOption(v))
  }

  return (
    <RfidSelectorContainer
      className="rfid-selector"
    >
      <Select
        dropdownClassName={dropdownClassName}
        disabled={loading}
        showSearch
        mode="multiple"
        allowClear
        filterOption={filterOption}
        loading={fetching}
        style={{ minWidth: 250, width: '100%', lineHeight: '34px' }}
        placeholder="RFID"
        onClear={handleOnClear}
        onChange={handleChange}
        onSearch={debounceFetcher}
        dropdownMatchSelectWidth={false}
        maxTagCount={0}
        onMouseDown={(e) => handleMouseDown(e)}
        maxTagPlaceholder={handlePlaceholder}
        removeIcon={removeIconSVG}
        notFoundContent={fetching ? <Spin size="small" /> : 'Start typing to search'}
      >
        {renderRfidList(rfids)}
      </Select>
    </RfidSelectorContainer>
  )
}